import { useCallback, useMemo } from "react";
import { useDelete, useUrlSearch } from "@/shared/hooks";
import { useInfiniteThemesQuery } from "../api/queries/index";
import { useTogglePublishedMutation } from "../api/mutations/index";
import { searchValues } from "../lib/options";
import type { SearchValues, Theme } from "../types";

interface UseThemesPageReturn {
  /** Applied (committed) search — pass straight to `<ThemeGallery search={…} />`. */
  appliedSearch: SearchValues;
  totalItems: number;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
  search: { show: boolean; values: SearchValues };
  updateSearch: (updates: { show?: boolean; values?: SearchValues }) => void;
  handleSearchSubmit: () => void;
  handleSearchReset: () => void;
  handleDelete: (id: number) => Promise<void>;
  handleTogglePublish: (id: number, value: boolean) => Promise<void>;
}

export const useThemesPage = (): UseThemesPageReturn => {
  const {
    appliedValues,
    draftValues,
    updateDraft,
    applySearch,
    resetSearch,
    showFilters,
    setShowFilters,
  } = useUrlSearch<SearchValues>({ defaultValues: searchValues });

  // Same query key the gallery uses — React Query dedupes, so this observer
  // just reads the count/error and stays in sync with the gallery's pages.
  const { data, isLoading, error, refetch } =
    useInfiniteThemesQuery(appliedValues);

  const themes = useMemo<Theme[]>(
    () => data?.pages.flatMap((page) => page.items ?? []) ?? [],
    [data],
  );
  const totalItems = data?.pages[0]?.totalCount ?? 0;

  const togglePublishMutation = useTogglePublishedMutation();

  const updateSearch = useCallback(
    (updates: { show?: boolean; values?: SearchValues }) => {
      if (updates.show !== undefined) setShowFilters(updates.show);
      if (updates.values) {
        (
          Object.entries(updates.values) as [keyof SearchValues, string][]
        ).forEach(([key, value]) => updateDraft(key, value));
      }
    },
    [setShowFilters, updateDraft],
  );

  const { deleteHandler } = useDelete(themes, refetch, "themes");

  const handleTogglePublish = useCallback(
    async (id: number, value: boolean) => {
      await togglePublishMutation.mutateAsync({ id, value });
    },
    [togglePublishMutation],
  );

  return {
    appliedSearch: appliedValues,
    totalItems,
    isLoading,
    error: error as Error | null,
    refetch,
    search: { show: showFilters, values: draftValues },
    updateSearch,
    handleSearchSubmit: applySearch,
    handleSearchReset: resetSearch,
    handleDelete: deleteHandler,
    handleTogglePublish,
  };
};
